import React, { useState } from 'react';
import { Bell, X, Minus, Plus, ShoppingCart, BellOff } from 'lucide-react';
import { Product } from '../../types';

interface Props {
  product: Product;
  cartQty: number;
  isAlreadyActive: boolean;
  onSave: (qty: number) => Promise<void>;
  onClose: () => void;
}

export function AvvisamiModal({ product, cartQty, isAlreadyActive, onSave, onClose }: Props) {
  const [qty, setQty] = useState(1);
  const [saving, setSaving] = useState(false);

  const changeQty = (delta: number) => setQty(q => Math.max(1, q + delta));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(qty);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="avvisami-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" />

      {/* Dialog */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
              <Bell className="w-4.5 h-4.5 text-red-700" />
            </div>
            <div>
              <h2 className="text-base font-semibold">Avvisami quando disponibile</h2>
              <p className="text-xs text-muted-foreground mt-0.5">Riceverai una notifica appena il prodotto torna in stock</p>
            </div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1 rounded-lg hover:bg-secondary/60 transition-colors" data-testid="avvisami-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Prodotto */}
        <div className="rounded-xl border bg-[hsl(var(--dtk-canvas))] p-3 mb-5">
          <div className="text-[10px] font-mono text-muted-foreground mb-0.5">{product.sku}</div>
          <div className="text-sm font-medium leading-snug">{product.title}</div>
          {product.brand && (
            <div className="mt-1.5">
              <span className="text-[10px] px-1.5 py-0.5 rounded border bg-secondary/60 font-medium">{product.brand}</span>
            </div>
          )}
        </div>

        {/* Info carrello */}
        {cartQty > 0 && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <ShoppingCart className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            <span>Hai già <strong>{cartQty} pz</strong> nel carrello — verranno mantenuti</span>
          </div>
        )}

        {/* Stato avviso attivo */}
        {isAlreadyActive && (
          <div className="flex items-center gap-2 text-xs text-emerald-700 mb-4 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <BellOff className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Hai già un avviso attivo per questo prodotto. Salvando aggiornerai la quantità.</span>
          </div>
        )}

        {/* Selettore quantità */}
        <div className="mb-6">
          <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Quantità desiderata
          </label>
          <div className="flex items-center gap-3">
            <div className="flex items-center border-2 border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => changeQty(-1)}
                disabled={qty <= 1}
                className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:bg-secondary/60 transition-colors disabled:opacity-40"
                data-testid="avvisami-qty-minus"
              >
                <Minus className="w-3.5 h-3.5" />
              </button>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={qty}
                onChange={e => { const v = parseInt(e.target.value) || 1; setQty(Math.max(1, v)); }}
                onFocus={e => e.target.select()}
                className="w-14 h-10 text-center text-sm font-bold bg-transparent focus:outline-none tabular-nums [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="avvisami-qty-input"
              />
              <button
                onClick={() => changeQty(1)}
                className="w-10 h-10 flex items-center justify-center text-muted-foreground hover:bg-secondary/60 transition-colors"
                data-testid="avvisami-qty-plus"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
            <span className="text-xs text-muted-foreground">pezzi</span>
          </div>
        </div>

        {/* Azioni */}
        <div className="flex gap-2.5">
          <button
            onClick={onClose}
            className="flex-1 h-10 rounded-xl border text-sm font-medium hover:bg-secondary/60 transition-colors"
            data-testid="avvisami-btn-chiudi"
          >
            Chiudi
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 h-10 rounded-xl bg-red-700 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-800 transition-colors disabled:opacity-60"
            data-testid="avvisami-btn-salva"
          >
            {saving ? (
              <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            ) : (
              <Bell className="w-4 h-4" />
            )}
            {saving ? 'Salvataggio…' : 'Salva avviso'}
          </button>
        </div>
      </div>
    </div>
  );
}
